/**
 * RevenueSharingStatementPage — `/reports/revenue-sharing`.
 *
 * Wired to GET /v1/reports/revenue-sharing?franchiseId=&period= on
 * service-reporting-api. Note: franchise settlement data is not yet in
 * SourceReaders — this report returns stub data until loadFranchiseSettlements() is wired.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banknote } from 'lucide-react';

import { getRevenueSharingReport } from '../../lib/api/pre-built-reports';
import { ApiError } from '../../lib/api/client';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice } from '../shared/StaffUI';
import { KPICard, formatNaira } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

export function RevenueSharingStatementPage(): JSX.Element {
  const report = findPreBuiltReport('revenue-sharing');
  const [franchiseId, setFranchiseId] = useState<string>('');
  const [period, setPeriod] = useState<string>('THIS_MONTH');

  const query = useQuery({
    queryKey: ['pre-built', 'revenue-sharing', franchiseId, period],
    queryFn: () => getRevenueSharingReport({ franchiseId: franchiseId || undefined, period }),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const data = query.data;
  const denied =
    query.isError && query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403);

  return (
    <ReportShell report={report}>
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Franchise ID</label>
            <input type="text" value={franchiseId} onChange={(e) => setFranchiseId(e.target.value)}
              placeholder="Leave blank for all" aria-label="Franchise ID"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Period</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white">
              <option value="THIS_MONTH">This month</option>
              <option value="LAST_MONTH">Last month</option>
              <option value="THIS_YEAR">This year</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Revenue lines" value={data ? data.lines.length.toLocaleString('en-US') : '—'} hint="Franchise" icon={Banknote} />
        <KPICard title="Total payout" value={data ? formatNaira(data.totalPayoutMinor) : '—'} hint="franchise.revenue-share.v1" tone="success" />
        <KPICard title="Franchise" value={data?.franchiseId ?? 'All'} hint="filter applied" />
        <KPICard title="Period" value={data?.period ?? '—'} hint="from–to" />
      </div>

      {query.isPending && <p className="text-sm text-muted">Loading report…</p>}
      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/readRevenueSharing</code> required.
        </DeferredNotice>
      )}
      {query.isError && !denied && (
        <DeferredNotice title="Report unavailable">{query.error.message}</DeferredNotice>
      )}

      {data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Payout breakdown</h3>
            <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
              Preview data — full data coming soon
            </span>
          </div>
          {data.lines.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">No revenue-sharing lines found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Service</th>
                    <th className="text-right px-4 py-2">Revenue</th>
                    <th className="text-right px-4 py-2">Commission %</th>
                    <th className="text-right px-4 py-2">Payout</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.lines.map((l) => (
                    <tr key={l.service}>
                      <td className="px-4 py-2 text-xs">{l.service}</td>
                      <td className="px-4 py-2 text-right text-xs">{formatNaira(l.revenueMinor)}</td>
                      <td className="px-4 py-2 text-right text-xs">{l.commissionPct}%</td>
                      <td className="px-4 py-2 text-right text-xs font-medium">{formatNaira(l.payoutMinor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ReportShell>
  );
}
